import React, { useState, useEffect, useRef, useCallback } from 'react';

import {
  Bot,
  Atom,
  FlaskConical,
  TrendingUp,
  GitFork,
  GraduationCap,
  RotateCcw,
  HelpCircle,
  Award,
  Wrench,
  ChevronLeft,
  ChevronRight,
  Lock,
  Sparkles,
  ArrowRight,
  Crown,
  CheckCircle2,
  Clock,
  Zap,
  Target,
  LineChart,
  Layers,
  BookOpen
} from 'lucide-react';

// ==========================================
// 10 CAROUSEL SLIDES CONFIGURATION
// ==========================================

const CAROUSEL_SLIDES = [
  {
    id: 'simulations',
    badge: 'INTERACTIVE LEARNING',
    headline: 'See concepts come alive.',
    description: 'Explore physics concepts through interactive real-time simulations.',
    cta: 'Explore Simulations →',
    isPremium: true,
    route: '/simulations',
    theme: 'from-purple-500/20 via-indigo-600/10 to-blue-950/40',
    borderColor: 'border-purple-500/40',
    accentGlow: 'rgba(168, 85, 247, 0.3)',
    visualType: 'atom-sim'
  },
  {
    id: 'what-if',
    badge: 'EXPERIMENT • LEARN • DISCOVER',
    headline: 'Change one rule. Watch reality react.',
    description: 'Experiment with concepts and understand what really changes when conditions change.',
    cta: 'Open What If? Lab →',
    isPremium: true,
    route: '/what-if',
    theme: 'from-emerald-500/20 via-teal-600/10 to-blue-950/40',
    borderColor: 'border-emerald-500/40',
    accentGlow: 'rgba(16, 185, 129, 0.3)',
    visualType: 'flask-lab'
  },
  {
    id: 'pyq-predictor',
    badge: 'JEE + NEET',
    headline: 'Know what matters most.',
    description: 'Explore chapter-wise PYQ trends, question frequency, recent patterns and prediction insights.',
    cta: 'Explore PYQ Predictor →',
    isPremium: true,
    route: '/pyq-predictor',
    theme: 'from-amber-500/20 via-orange-600/10 to-rose-950/40',
    borderColor: 'border-amber-500/40',
    accentGlow: 'rgba(245, 158, 11, 0.3)',
    visualType: 'trend-chart'
  },
  {
    id: 'prerequisites',
    badge: 'SMART PREPARATION',
    headline: "Don't start a chapter blindly.",
    description: 'Know the important basics and prerequisites before you begin a chapter.',
    cta: 'Check Prerequisites →',
    isPremium: true,
    route: '/prerequisites',
    theme: 'from-blue-500/20 via-indigo-600/10 to-purple-950/40',
    borderColor: 'border-blue-500/40',
    accentGlow: 'rgba(59, 130, 246, 0.3)',
    visualType: 'roadmap-nodes'
  },
  {
    id: 'college-predictor',
    badge: 'COLLEGE PLANNING',
    headline: 'From rank to college.',
    description: 'Explore college and cutoff insights for JEE & NEET and plan your target smarter.',
    cta: 'Open College Predictor →',
    isPremium: true,
    route: '/college-predictor',
    theme: 'from-indigo-500/20 via-purple-600/10 to-pink-950/40',
    borderColor: 'border-indigo-500/40',
    accentGlow: 'rgba(99, 102, 241, 0.3)',
    visualType: 'college-badge'
  },
  {
    id: 'revision',
    badge: 'SMART REVISION',
    headline: 'Revise what actually needs revision.',
    description: 'Stay on top of your revision workflow and keep important topics from slipping away.',
    cta: 'Start Revision →',
    isPremium: false,
    route: '/revision',
    theme: 'from-teal-500/20 via-cyan-600/10 to-blue-950/40',
    borderColor: 'border-teal-500/40',
    accentGlow: 'rgba(20, 184, 166, 0.3)',
    visualType: 'revision-ring'
  },
  {
    id: 'questions',
    badge: 'PRACTICE',
    headline: 'Practice. Track. Improve.',
    description: 'Use your question practice system to strengthen your preparation.',
    cta: 'Practice Questions →',
    isPremium: false,
    route: '/questions',
    theme: 'from-sky-500/20 via-blue-600/10 to-indigo-950/40',
    borderColor: 'border-sky-500/40',
    accentGlow: 'rgba(14, 165, 233, 0.3)',
    visualType: 'question-cards'
  },
  {
    id: 'tests',
    badge: 'TEST YOURSELF',
    headline: 'Turn preparation into performance.',
    description: 'Test your preparation and identify where you actually stand.',
    cta: 'Take a Test →',
    isPremium: false,
    route: '/tests',
    theme: 'from-violet-500/20 via-fuchsia-600/10 to-purple-950/40',
    borderColor: 'border-violet-500/40',
    accentGlow: 'rgba(139, 92, 246, 0.3)',
    visualType: 'test-timer'
  },
  {
    id: 'toolkit',
    badge: 'SMART TOOLS',
    headline: 'Everything you need, in one toolkit.',
    description: 'Access useful formulas, tables, calculators and preparation tools.',
    cta: 'Open JEE Toolkit →',
    isPremium: true,
    route: '/toolkit',
    theme: 'from-rose-500/20 via-pink-600/10 to-purple-950/40',
    borderColor: 'border-rose-500/40',
    accentGlow: 'rgba(244, 63, 94, 0.3)',
    visualType: 'tools-matrix'
  }
];

// ==========================================
// EYE-CATCHING ANIMATED GRAPHIC ILLUSTRATIONS
// ==========================================

type CarouselSlide = (typeof CAROUSEL_SLIDES)[number];

type BannerProps = {
  onNavigate?: (route: string) => void;
  isUserSubscribed?: boolean;
  onOpenUpgradeModal?: (slide: CarouselSlide) => void;
};

const AnimatedVisual = ({ type }: { type: string }) => {
  switch (type) {
    case 'ai-orb':
      return (
        <div className="relative flex items-center justify-center w-full h-32 sm:h-40">
          {/* Pulsing Glowing Aura */}
          <div className="absolute w-28 h-28 sm:w-36 sm:h-36 rounded-full bg-cyan-500/30 blur-2xl animate-pulse" />
          
          {/* Animated Orbiting Ring */}
          <div className="absolute w-28 h-28 sm:w-32 sm:h-32 rounded-full border-2 border-dashed border-cyan-400/50 animate-spin" style={{ animationDuration: '12s' }} />
          <div className="absolute w-24 h-24 sm:w-28 sm:h-28 rounded-full border border-blue-400/30 animate-ping" style={{ animationDuration: '4s' }} />
          {/* Central Holographic AI Orb */}
          <div className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-3xl border border-cyan-300/50 bg-gradient-to-tr from-cyan-950 via-blue-900 to-indigo-900 backdrop-blur-xl flex flex-col items-center justify-center shadow-xl shadow-cyan-500/30">
            <Bot className="w-10 h-10 sm:w-12 sm:h-12 text-cyan-300 animate-bounce" style={{ animationDuration: '2.5s' }} />
            <span className="text-[9px] font-mono font-bold text-cyan-200 mt-0.5 tracking-wider">AI ASSIST</span>
            
            {/* Floating Sparkles */}
            <Sparkles className="absolute -top-2 -right-2 w-6 h-6 text-cyan-300 animate-spin" style={{ animationDuration: '6s' }} />
            <Zap className="absolute -bottom-1 -left-2 w-5 h-5 text-amber-300 animate-pulse" />
          </div>
        </div>
      );
    case 'atom-sim':
      return (
        <div className="relative flex items-center justify-center w-full h-32 sm:h-40">
          <div className="absolute w-28 h-28 sm:w-36 sm:h-36 rounded-full bg-purple-500/30 blur-2xl animate-pulse" />
          
          {/* Rotating Atomic Orbits */}
          <div className="relative w-28 sm:w-32 h-28 sm:h-32 flex items-center justify-center">
            <div className="absolute w-28 sm:w-32 h-10 border-2 border-purple-400/60 rounded-full transform rotate-45 animate-spin" style={{ animationDuration: '6s' }} />
            <div className="absolute w-28 sm:w-32 h-10 border-2 border-indigo-400/60 rounded-full transform -rotate-45 animate-spin" style={{ animationDuration: '5s' }} />
            <div className="absolute w-28 sm:w-32 h-10 border-2 border-pink-400/60 rounded-full transform rotate-90 animate-spin" style={{ animationDuration: '8s' }} />
            
            {/* Central Nucleus */}
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 via-pink-500 to-indigo-500 flex items-center justify-center shadow-lg shadow-purple-500/80 animate-pulse">
              <Atom className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
      );
    case 'flask-lab':
      return (
        <div className="relative flex items-center justify-center w-full h-32 sm:h-40">
          <div className="absolute w-28 h-28 sm:w-36 sm:h-36 rounded-full bg-emerald-500/30 blur-2xl animate-pulse" />
          
          <div className="relative p-4 rounded-3xl border border-emerald-400/50 bg-emerald-950/80 backdrop-blur-xl flex flex-col items-center justify-center shadow-xl shadow-emerald-500/30">
            <FlaskConical className="w-12 h-12 text-emerald-300 animate-bounce" style={{ animationDuration: '2.8s' }} />
            
            {/* Bubbling Visual Animation */}
            <div className="flex gap-2 mt-1">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-ping" style={{ animationDuration: '1s' }} />
              <span className="w-2.5 h-2.5 rounded-full bg-teal-300 animate-ping" style={{ animationDuration: '1.4s' }} />
              <span className="w-2.5 h-2.5 rounded-full bg-cyan-300 animate-ping" style={{ animationDuration: '1.8s' }} />
            </div>
          </div>
        </div>
      );
    case 'trend-chart':
      return (
        <div className="relative flex items-center justify-center w-full h-32 sm:h-40">
          <div className="absolute w-28 h-28 sm:w-36 sm:h-36 rounded-full bg-amber-500/30 blur-2xl animate-pulse" />
          
          <div className="relative w-32 sm:w-36 p-3.5 rounded-2xl border border-amber-400/50 bg-amber-950/80 backdrop-blur-xl flex flex-col gap-2 shadow-xl shadow-amber-500/30">
            <div className="flex items-center justify-between border-b border-amber-500/30 pb-1.5">
              <span className="text-[10px] text-amber-300 font-mono font-bold tracking-wider">PYQ ANALYSIS</span>
              <TrendingUp className="w-4 h-4 text-amber-400 animate-pulse" />
            </div>
            
            {/* Equalizer Bars */}
            <div className="flex items-end gap-2 h-12 pt-1">
              <div className="w-1/4 bg-amber-500/40 rounded-t h-40% animate-pulse" />
              <div className="w-1/4 bg-amber-500/60 rounded-t h-65% animate-pulse" style={{ animationDelay: '200ms' }} />
              <div className="w-1/4 bg-amber-500/80 rounded-t h-85% animate-pulse" style={{ animationDelay: '400ms' }} />
              <div className="w-1/4 bg-gradient-to-t from-amber-500 to-amber-300 rounded-t h-100% animate-bounce shadow-lg shadow-amber-400/50" style={{ animationDuration: '2s' }} />
            </div>
          </div>
        </div>
      );
    case 'roadmap-nodes':
      return (
        <div className="relative flex items-center justify-center w-full h-32 sm:h-40">
          <div className="absolute w-28 h-28 sm:w-36 sm:h-36 rounded-full bg-blue-500/30 blur-2xl animate-pulse" />
          
          <div className="relative flex flex-col gap-2">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl border border-blue-400/40 bg-blue-950/80 backdrop-blur-md text-xs text-blue-200">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              <span>Basic Integration</span>
            </div>
            <div className="w-0.5 h-3 bg-cyan-400/80 ml-5 animate-pulse" />
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl border border-cyan-400/60 bg-cyan-950/90 backdrop-blur-md text-xs text-white font-bold shadow-lg shadow-cyan-500/30">
              <GitFork className="w-4 h-4 text-cyan-300 animate-spin" style={{ animationDuration: '8s' }} />
              <span>Differential Equations</span>
            </div>
          </div>
        </div>
      );
    case 'college-badge':
      return (
        <div className="relative flex items-center justify-center w-full h-32 sm:h-40">
          <div className="absolute w-28 h-28 sm:w-36 sm:h-36 rounded-full bg-indigo-500/30 blur-2xl animate-pulse" />
          
          <div className="relative p-4 rounded-3xl border border-indigo-400/50 bg-indigo-950/80 backdrop-blur-xl flex flex-col items-center justify-center gap-2 shadow-xl shadow-indigo-500/30">
            <GraduationCap className="w-11 h-11 text-indigo-300 animate-bounce" style={{ animationDuration: '2.5s' }} />
            <div className="px-2.5 py-1 rounded-full bg-indigo-500/30 border border-indigo-400/40 text-[10px] text-indigo-200 font-bold tracking-wider">
              IIT / AIIMS CUTOFFS
            </div>
          </div>
        </div>
      );
    case 'revision-ring':
      return (
        <div className="relative flex items-center justify-center w-full h-32 sm:h-40">
          <div className="absolute w-28 h-28 sm:w-36 sm:h-36 rounded-full bg-teal-500/30 blur-2xl animate-pulse" />
          
          <div className="relative w-22 h-22 sm:w-24 sm:h-24 rounded-full border-4 border-teal-500/30 border-t-teal-400 border-r-cyan-400 animate-spin flex items-center justify-center bg-teal-950/70 backdrop-blur-xl shadow-xl shadow-teal-500/30" style={{ animationDuration: '6s' }}>
            <RotateCcw className="w-8 h-8 text-teal-300" />
          </div>
        </div>
      );
    case 'question-cards':
      return (
        <div className="relative flex items-center justify-center w-full h-32 sm:h-40">
          <div className="absolute w-28 h-28 sm:w-36 sm:h-36 rounded-full bg-sky-500/30 blur-2xl animate-pulse" />
          
          <div className="relative w-28 sm:w-32 h-24 border border-sky-400/50 bg-sky-950/80 rounded-2xl p-3 backdrop-blur-xl flex flex-col justify-between shadow-xl shadow-sky-500/30">
            <div className="flex justify-between items-center">
              <span className="text-xs text-sky-300 font-bold">PRACTICE Q.</span>
              <HelpCircle className="w-4 h-4 text-sky-400 animate-bounce" />
            </div>
            <div className="w-full h-1.5 bg-slate-800 rounded-full overflow-hidden">
              <div className="h-full bg-sky-400 w-4/5 animate-pulse" />
            </div>
            <span className="text-[10px] text-sky-200 font-mono font-bold">Accuracy: 94%</span>
          </div>
        </div>
      );
    case 'test-timer':
      return (
        <div className="relative flex items-center justify-center w-full h-32 sm:h-40">
          <div className="absolute w-28 h-28 sm:w-36 sm:h-36 rounded-full bg-violet-500/30 blur-2xl animate-pulse" />
          
          <div className="relative p-4 rounded-3xl border border-violet-400/50 bg-violet-950/80 backdrop-blur-xl flex flex-col items-center justify-center gap-1.5 shadow-xl shadow-violet-500/30">
            <Award className="w-10 h-10 text-violet-300 animate-bounce" style={{ animationDuration: '2.2s' }} />
            <div className="flex items-center gap-1 text-[11px] font-mono text-violet-200 font-bold">
              <Clock className="w-3.5 h-3.5 animate-spin" style={{ animationDuration: '8s' }} />
              <span>REAL TEST SIM</span>
            </div>
          </div>
        </div>
      );
    case 'tools-matrix':
      return (
        <div className="relative flex items-center justify-center w-full h-32 sm:h-40">
          <div className="absolute w-28 h-28 sm:w-36 sm:h-36 rounded-full bg-rose-500/30 blur-2xl animate-pulse" />
          
          <div className="relative grid grid-cols-2 gap-2 p-2.5 rounded-2xl border border-rose-400/50 bg-rose-950/80 backdrop-blur-xl shadow-xl shadow-rose-500/30">
            <div className="p-2 bg-rose-900/50 rounded-xl text-center flex items-center justify-center">
              <Wrench className="w-5 h-5 text-rose-300 animate-spin" style={{ animationDuration: '6s' }} />
            </div>
            <div className="p-2 bg-rose-900/50 rounded-xl text-center flex items-center justify-center">
              <span className="text-xs text-rose-200 font-mono font-bold">E=mc²</span>
            </div>
          </div>
        </div>
      );
    default:
      return null;
  }
};

// ==========================================
// FEATURE SHOWCASE BANNER COMPONENT
// ==========================================

export default function FeatureShowcaseBanner({
  onNavigate = (route: string) => {
    if (typeof window !== 'undefined') window.location.href = route;
  },
  isUserSubscribed = false,
  onOpenUpgradeModal = (slide: CarouselSlide) => {
    alert(`Unlock Quantum Pro to access ${slide.badge}`);
  }
}: BannerProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const totalSlides = CAROUSEL_SLIDES.length;
  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % totalSlides);
  }, [totalSlides]);
  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + totalSlides) % totalSlides);
  }, [totalSlides]);
  // Auto-rotating every 4.5 seconds
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      goToNext();
    }, 4500);
    return () => clearInterval(interval);
  }, [isPaused, goToNext]);
  // Touch Swipe for Mobile
  const handleTouchStart = (e: React.TouchEvent<HTMLElement>) => {
    setIsPaused(true);
    touchStartX.current = e.targetTouches[0].clientX;
  };
  const handleTouchMove = (e: React.TouchEvent<HTMLElement>) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };
  const handleTouchEnd = () => {
    setIsPaused(false);
    if (!touchStartX.current || !touchEndX.current) return;
    const distance = touchStartX.current - touchEndX.current;
    if (distance > 40) goToNext();
    if (distance < -40) goToPrev();
    touchStartX.current = 0;
    touchEndX.current = 0;
  };
  const handleSlideClick = (slide: CarouselSlide) => {
    if (slide.isPremium && !isUserSubscribed) {
      onOpenUpgradeModal(slide);
    } else {
      onNavigate(slide.route);
    }
  };
  const currentSlide = CAROUSEL_SLIDES[currentIndex];
  return (
    <section
      aria-label="Quantum Feature Showcase"
      className="relative w-full my-6 select-none"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Outer Glow & Glass Box */}
      <div
        className={`relative overflow-hidden rounded-[24px] sm:rounded-[28px] border ${currentSlide.borderColor} bg-slate-950/90 backdrop-blur-xl shadow-2xl transition-all duration-700 ease-in-out min-h-[190px] sm:min-h-[220px] flex flex-col justify-between`}
        style={{
          boxShadow: `0 10px 40px -10px ${currentSlide.accentGlow}`
        }}
      >
        {/* Dynamic Background Gradient Overlay */}
        <div
          className={`absolute inset-0 bg-gradient-to-r ${currentSlide.theme} opacity-85 transition-all duration-700`}
        />
        {/* Top Glass Reflection Header Line */}
        <div className="absolute top-0 inset-x-0 h-[1px] bg-gradient-to-r from-transparent via-white/30 to-transparent" />
        {/* Slide Content Area */}
        <div className="relative z-10 p-4 sm:p-6 md:p-7 flex items-center justify-between gap-4 sm:gap-6 h-full">
          {/* Left Text & Interactive CTA */}
          <div className="flex-1 max-w-xl flex flex-col justify-center">
            {/* Category Badge & Membership Requirement Tag */}
            <div className="flex items-center gap-2 mb-1.5 sm:mb-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] sm:text-xs font-bold tracking-wider uppercase bg-white/10 text-cyan-300 border border-cyan-400/20 backdrop-blur-md">
                {currentSlide.badge}
              </span>
              {currentSlide.isPremium && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-500/20 text-amber-300 border border-amber-500/30">
                  <Crown className="w-3 h-3 text-amber-400" />
                  PRO
                </span>
              )}
            </div>
            {/* Headline */}
            <h2 className="text-base sm:text-2xl md:text-3xl font-extrabold text-white tracking-tight line-clamp-1">
              {currentSlide.headline}
            </h2>
            {/* Description */}
            <p className="mt-1 sm:mt-2 text-xs sm:text-sm text-slate-300 line-clamp-2 leading-relaxed max-w-lg">
              {currentSlide.description}
            </p>
            {/* CTA Button */}
            <div className="mt-3 sm:mt-4 flex items-center">
              <button
                onClick={() => handleSlideClick(currentSlide)}
                className="group relative inline-flex items-center gap-2 px-3.5 py-1.5 sm:px-5 sm:py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white text-xs sm:text-sm font-semibold transition-all duration-300 shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 active:scale-95 focus:outline-none"
              >
                <span>{currentSlide.cta}</span>
                {currentSlide.isPremium && !isUserSubscribed ? (
                  <Lock className="w-3.5 h-3.5 text-amber-300" />
                ) : (
                  <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                )}
              </button>
            </div>
          </div>
          {/* Right Visual Graphic Area */}
          <div className="hidden xs:flex sm:flex items-center justify-center w-28 sm:w-44 md:w-56 shrink-0 relative">
            <AnimatedVisual type={currentSlide.visualType} />
          </div>
        </div>
        {/* Footer Navigation Bar (Dots + Arrows) */}
        <div className="relative z-10 px-4 sm:px-6 pb-3 pt-1 flex items-center justify-between">
          {/* Pagination Indicators */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            {CAROUSEL_SLIDES.map((slide, idx) => (
              <button
                key={slide.id}
                onClick={() => setCurrentIndex(idx)}
                aria-label={`Go to slide ${idx + 1}`}
                className={`transition-all duration-300 rounded-full focus:outline-none ${
                  idx === currentIndex
                    ? 'w-6 sm:w-8 h-2 bg-gradient-to-r from-cyan-400 to-blue-500 shadow-sm shadow-cyan-400/50'
                    : 'w-2 h-2 bg-slate-700 hover:bg-slate-500'
                }`}
              />
            ))}
          </div>
          {/* Navigation Arrows */}
          <div className="flex items-center gap-1 sm:gap-2">
            <button
              onClick={goToPrev}
              aria-label="Previous Slide"
              className="p-1.5 sm:p-2 rounded-full bg-slate-900/60 border border-slate-700/50 text-slate-300 hover:text-white hover:bg-slate-800 transition-colors focus:outline-none"
            >
              <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
            <button
              onClick={goToNext}
              aria-label="Next Slide"
              className="p-1.5 sm:p-2 rounded-full bg-slate-900/60 border border-slate-700/50 text-slate-300 hover:text-white hover:bg-slate-800 transition-colors focus:outline-none"
            >
              <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
